// Perceptual gradient interpolation following Bujack et al. (2025)
// "The Geometry of Color in the Light of a Non-Riemannian Space" and
// the OKLab perceptual colour space from Björn Ottosson (2020).
//
// Use case: any colour-to-colour `mix()` in a fragment shader is a
// straight line in linear sRGB. For pairs of colours with different
// hues (e.g. green → magenta, blue → cyan) that line slices through
// the perceptually desaturated centre, producing muddy grey-brown
// mid-tones. OKLCh (the polar form of OKLab) interpolates along the
// perceptual hue arc with shortest-arc rotation, which keeps the
// mid-tones vivid the way real photography shows them.
//
// Inject this constant into any shader source via template literal:
//
//   import { OKLAB_MIX_GLSL } from './oklab.js';
//   const fragmentShader = `
//     ${OKLAB_MIX_GLSL}
//     ...
//     vec3 color = oklchMix(c1, c2, t);
//   `;
//
// Cost: ~6 matrix muls + cbrt() per direction. Cheap enough for
// per-fragment use without measurement.
export const OKLAB_MIX_GLSL = `
vec3 _linSrgbToOklab(vec3 c) {
  float l = 0.4122214708 * c.r + 0.5363325363 * c.g + 0.0514459929 * c.b;
  float m = 0.2119034982 * c.r + 0.6806995451 * c.g + 0.1073969566 * c.b;
  float s = 0.0883024619 * c.r + 0.2817188376 * c.g + 0.6299787005 * c.b;
  float l_ = pow(l, 1.0 / 3.0);
  float m_ = pow(m, 1.0 / 3.0);
  float s_ = pow(s, 1.0 / 3.0);
  return vec3(
    0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
    1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
    0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_
  );
}
vec3 _oklabToLinSrgb(vec3 c) {
  float l_ = c.x + 0.3963377774 * c.y + 0.2158037573 * c.z;
  float m_ = c.x - 0.1055613458 * c.y - 0.0638541728 * c.z;
  float s_ = c.x - 0.0894841775 * c.y - 1.2914855480 * c.z;
  float l = l_ * l_ * l_;
  float m = m_ * m_ * m_;
  float s = s_ * s_ * s_;
  return vec3(
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s
  );
}
// Shortest-arc cylindrical OKLab (OKLCh) interpolation. Linear in L
// and C, angular in hue with the |dh| <= PI wrap so the rotation
// goes the short way around the perceptual hue circle.
vec3 oklchMix(vec3 a, vec3 b, float t) {
  vec3 la = _linSrgbToOklab(max(a, 0.0));
  vec3 lb = _linSrgbToOklab(max(b, 0.0));
  float La = la.x, Lb = lb.x;
  float Ca = length(la.yz), Cb = length(lb.yz);
  float ha = atan(la.z, la.y);
  float hb = atan(lb.z, lb.y);
  float dh = hb - ha;
  if (dh >  3.141592653589793) dh -= 6.283185307179586;
  if (dh < -3.141592653589793) dh += 6.283185307179586;
  float L = mix(La, Lb, t);
  float C = mix(Ca, Cb, t);
  float h = ha + dh * t;
  return _oklabToLinSrgb(vec3(L, C * cos(h), C * sin(h)));
}
// A straight line across OKLab, for a veil laid over a surface of any hue.
// The exception to OKLCh by default: Earth's aerial-perspective haze
// (earth.js) mixes the ground toward a blue-grey veil, and a desert's orange
// sits nearly opposite that blue on the hue circle, so the shortest arc
// flipped from pixel to pixel through pink one way and lime the other. A
// veil adds light of its own colour; it has no reason to turn the ground's
// hue, and the straight line keeps each end's own hue all the way across.
vec3 oklabMix(vec3 a, vec3 b, float t) {
  return _oklabToLinSrgb(mix(_linSrgbToOklab(max(a, 0.0)), _linSrgbToOklab(max(b, 0.0)), t));
}
// oklchMix for a mix where one end can be near grey: white cloud over the
// sea. A near-grey colour has no hue to speak of: its atan is whatever the
// last bits of a and b say, and rotating from that toward the other
// colour's hue sweeps the mix through hues neither end has (partly cloudy
// ocean went lilac where white cloud met blue sea). When either chroma is
// that small the mix runs straight across OKLab instead, which carries the
// chromatic end's own hue all the way (CSS Color 4 calls such a hue
// powerless), and chromatic pairs keep the arc. Kept apart from oklchMix
// because a pale colour such as the city lights' cool LED white is still
// meant to take the arc there.
vec3 oklchMixNeutral(vec3 a, vec3 b, float t) {
  vec3 la = _linSrgbToOklab(max(a, 0.0));
  vec3 lb = _linSrgbToOklab(max(b, 0.0));
  float Ca = length(la.yz), Cb = length(lb.yz);
  float ha = atan(la.z, la.y);
  float hb = atan(lb.z, lb.y);
  float dh = hb - ha;
  if (dh >  3.141592653589793) dh -= 6.283185307179586;
  if (dh < -3.141592653589793) dh += 6.283185307179586;
  float C = mix(Ca, Cb, t);
  float h = ha + dh * t;
  vec3 lch = vec3(mix(la.x, lb.x, t), C * cos(h), C * sin(h));
  return _oklabToLinSrgb(mix(mix(la, lb, t), lch, smoothstep(0.015, 0.05, min(Ca, Cb))));
}
`;
